import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUIContext } from "../UIContext/UIContext";
import { DateTime } from "luxon";
import { backendUrl } from "../../api/constants";
import axiosInstance from "../../api/axiosInstance";
import { convertEventDate } from "../../util/eventUtils";
import { enqueueSnackbar } from "notistack";
import { useData } from "../DataContext/DataProvider";
import isEqual from "lodash/isEqual";
import { debounce } from "lodash";
import { defaultDateFilter } from "../../util/dataUtils";
import { useUser } from "../UserContext/UserProvider";
import { trackTransformation, createDebugLogger, isDebugEvent, DEBUG_EVENT_NAMES } from "../../util/debugUtils";
import { useAnalytics } from "../AnalyticsContext/AnalyticsProvider";
const mapContext = createContext();

export const MapProvider = ({ children }) => {
  const [mapLocations, setMapLocations] = useState([]);
  const [mapPage, setMapPage] = useState({
    page: 1,
    limit: 10,
  });
  const [totalResults, setTotalResults] = useState();
  const [mapResults, setMapResults] = useState([]);
  const [searchResults, setSearchResults] = useState([]); // For flat event data when searching
  
  // Create debug logger for MapContext
  const mapDebugLogger = createDebugLogger("MapContext");
  const { likedFilter, posterFilter, activeCategories } = useUIContext();
  const [mapLoading, setMapLoading] = useState(false);
  const [mapPageLoading, setMapPageLoading] = useState(false);
  const { selectedTimezone, userLocation } = useData();
  const { user } = useUser();
  const [dateFilter, setDateFilter] = useState();
  const [tagFilter, setTagFilter] = useState([]);
  
  // Add search functionality from SearchContext
  const [searchQuery, setSearchQuery] = useState("");
  const [sortMode, setSortMode] = useState(null);
  const [autoSuggestion, setAutoSuggestion] = useState();
  const [activeTab, setActiveTab] = useState("event");
  // keep a ref of the current active tab to guard async responses
  const activeTabRef = useRef("event");
  // request cancellation to prevent race conditions
  const currentRequestRef = useRef(null);
  const requestIdRef = useRef(0);
  // track current request meta and a simple one-deep queue for pagination
  const currentRequestMetaRef = useRef(null); // { key: string, page: number }
  const pendingRequestRef = useRef(null); // holds opts to request next
  
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);
  const memoizedDateFilter = useMemo(
    () => defaultDateFilter(selectedTimezone),
    [selectedTimezone]
  );
  const previousFilter = useRef();
  const debouncedPerformSearch = useRef();
  const { trackSessionEvent } = useAnalytics();
  const prevSearchQueryRef = useRef('');

  // util: debounced value hook to avoid racing effects
  const useDebouncedValue = (value, delayMs) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
      const id = setTimeout(() => setDebounced(value), delayMs);
      return () => clearTimeout(id);
    }, [value, delayMs]);
    return debounced;
  };
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 400);
  const effectiveSortMode = useMemo(() => {
    const hasUserCoordinates = Boolean(userLocation?.lat && userLocation?.lng);
    const hasSearchQuery = Boolean(searchQuery?.trim());

    if (sortMode === "proximity" && !hasUserCoordinates) {
      return hasSearchQuery ? "relevance" : "date";
    }

    if (sortMode && (sortMode !== "relevance" || searchQuery?.trim())) {
      return sortMode;
    }

    return hasSearchQuery ? "relevance" : hasUserCoordinates ? "proximity" : "date";
  }, [sortMode, searchQuery, userLocation?.lat, userLocation?.lng]);
  
  // ANALYTICS: Track search queries
  useEffect(() => {
    // Only track if search query is not empty and has changed
    if (debouncedSearchQuery && debouncedSearchQuery.trim() !== '' && 
        debouncedSearchQuery !== prevSearchQueryRef.current) {
      
      console.log('🔍 [ANALYTICS] Map search performed:', {
        query: debouncedSearchQuery,
        activeTab,
        hasUser: !!user
      });
      
      trackSessionEvent({
        action: 'search performed',
        category: 'search',
        label: 'map_search',
        value: {
          query: debouncedSearchQuery.trim(),
          tab: activeTab,
          hasFilters: tagFilter.length > 0 || activeCategories.length > 0,
        },
        timestamp: Date.now(),
      });
      
      console.log('✅ [ANALYTICS] Search tracking COMPLETED for:', debouncedSearchQuery);
      
      prevSearchQueryRef.current = debouncedSearchQuery;
    }
    
    // Clear ref when search is cleared
    if (!debouncedSearchQuery || debouncedSearchQuery.trim() === '') {
      prevSearchQueryRef.current = '';
    }
  }, [debouncedSearchQuery, activeTab, trackSessionEvent, user, tagFilter.length, activeCategories.length]);

  const handleSpecialFilter = (locs) => {
    let filterFunction;
    const filter = dateFilter;
    switch (filter) {
      case "now":
        filterFunction = (ev, { nowZoned }) => {
          const date = ev.nextOccurence || ev.date;
          return date.start <= nowZoned && date.end >= nowZoned;
        };
        break;

      case "soon":
        filterFunction = (ev, { threeHoursFromNow, now }) => {
          const date = ev.nextOccurence || ev.date;
          return (
            (date.start < threeHoursFromNow && date.end > now) ||
            (date.end < threeHoursFromNow && date.end > now)
          );
        };
        break;

      case "later":
        filterFunction = (ev, { endOfToday, now }) => {
          const date = ev.nextOccurence || ev.date;
          return date.start <= endOfToday && date.end >= now;
        };
        break;

      case "late night":
        filterFunction = (ev, { tenPM, sixAM }) => {
          const date = ev.nextOccurence || ev.date;
          return date.end >= tenPM && date.start <= sixAM;
        };
        break;

      default:
        filterFunction = (e) => true;
        break;
    }

    // console.log("filterFunction ", filterFunction);
    const filtered = locs?.filter((e) => {
      const relativeDates = {
        now: DateTime.local(),
      };

      relativeDates.nowZoned = relativeDates.now.setZone(e.timezone[0]);

      relativeDates.threeHoursFromNow = relativeDates.now.plus({
        hours: 3,
      });

      relativeDates.endOfToday = relativeDates.nowZoned.endOf("day");
      relativeDates.tenPM = relativeDates.nowZoned.set({
        hour: 22,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      relativeDates.sixAM = relativeDates.nowZoned
        .plus({ days: 1 })
        .set({ hour: 6, minute: 0, second: 0, millisecond: 0 });

      if (e.type === "event") {
        return filterFunction(e, relativeDates);
      } else {
        e.events = e.events?.filter((o) => filterFunction(o, relativeDates));
        // if event._matchingEvent doesnt match the filter, then use the first event of the filtered list
        if (e._matchingEvent) {
          const matchingEvent = e.events.find(
            (ev) => ev._id === e._matchingEvent
          );
          if (matchingEvent) {
            e._matchingEvent = matchingEvent;
          } else {
            e._matchingEvent = e.events[0];
          }
        }

        return e.events?.length > 0;
      }
    });

    // console.log("filtered ", filtered);

    return filtered;
  };

  const fetchFilteredEvents = async (options) => {
    let opts = options;
    opts.setLoading && opts.setLoading(true);
    // set main loading only for initial loads (page 1); use page loader for pagination
    const nextPage = (opts && typeof opts.page === 'number') ? opts.page : (mapPage?.page || 1);
    if (nextPage === 1) {
      setMapLoading(true);
    }
    const specialDateFilter = ["now", "soon", "later", "late night"].includes(
      opts.dateFilter
    );
    // console.log("MapContext specialDateFilter ", specialDateFilter);
    if (specialDateFilter) {
      opts.specialDateFilter = true;
      const ddate = memoizedDateFilter;
      opts.from = ddate.start;
      opts.to = ddate.end;
    }

    opts.onSuccess = (results) => {
      const res = results?.data;
      // console.log("MapContext results ", res);
      let totalResults = results?.totalResults;
      if (res) {
        let presults;
        if (specialDateFilter) {
          presults = handleSpecialFilter(res);
          //resort results
          presults = presults?.sort((a, b) => {
            const aDate =
              a.nextOccurence?.start ||
              a.date?.start ||
              a._matchingEvent?.nextOccurence?.start ||
              a._matchingEvent?.date?.start;
            const bDate =
              b.nextOccurence?.start ||
              b.date?.start ||
              b._matchingEvent?.nextOccurence?.start ||
              b._matchingEvent?.date?.start;
            return aDate - bDate;
          });
          totalResults = presults.length;
          //minifedResults should also be updated
          results.minifiedResults = presults;
        } else {
          presults = res;
        }
        // console.log("MapContext presults ", presults);
        // atomically replace for page 1 to avoid flash of stale items
        setMapLocations((prev) => (mapPage.page === 1 ? presults : [...prev, ...presults]));
      }

      // console.log("MapContext totalResults after ", totalResults);
      setTotalResults(totalResults);
      // console.log("MapContext Minified Results : ", results?.minifiedResults);
      
      // Debug: Track debug events in minified results
      const minifiedResults = results?.minifiedResults;
      if (minifiedResults) {
        const debugVenues = minifiedResults.filter(venue => 
          venue.events?.some(eventId => DEBUG_EVENT_NAMES.some(name => name.includes(eventId))) ||
          (venue._matchingEvent && DEBUG_EVENT_NAMES.includes(venue._matchingEvent.name))
        );
        
        if (debugVenues.length > 0) {
          mapDebugLogger.log(`🗺️ MINIFIED RESULTS: Found ${debugVenues.length} venue(s) with debug events`);
          debugVenues.forEach(venue => {
            mapDebugLogger.log(`   Venue: "${venue.name}" (ID: ${venue._id})`);
            mapDebugLogger.log(`   Matching Event: "${venue._matchingEvent?.name || 'none'}"`);
            mapDebugLogger.log(`   Events Count: ${venue.events?.length || 0}`);
          });
        } else {
          mapDebugLogger.log(`🗺️ MINIFIED RESULTS: No debug events found in ${minifiedResults.length} venues`);
        }
      }
      
      setMapResults(results?.minifiedResults);
      // do not toggle mapLoading here; let the caller (performSearch) toggle after state updates
    };

    if (options.debounceSearch) {
      if (!debouncedPerformSearch.current) {
        debouncedPerformSearch.current = debounce(async (debouncedOpts) => {
          await performSearch(debouncedOpts);
        }, 500);
      }
      debouncedPerformSearch.current(opts);
    } else {
      await performSearch(opts);
    }
    // don't turn off here; performSearch will do so after committing new state
  };

  const refreshEvents = () => {
    // console.log("called refresh events ", {
    //   categories: activeCategories,
    //   from: dateFilter?.start,
    //   to: dateFilter?.end,
    //   dateFilter,
    //   tagFilter,
    //   searchQuery,
    //   activeTab,
    //   setLoading: setMapPageLoading,
    //   timezone: selectedTimezone,
    // });
    fetchFilteredEvents({
      categories: activeCategories,
      from: dateFilter?.start,
      to: dateFilter?.end,
      dateFilter,
      tagFilter,
      searchQuery,
      activeTab,
      setLoading: setMapPageLoading,
      timezone: selectedTimezone,
      sort: effectiveSortMode,
    });
  };

  const performSearch = async (options) => {
    try {
      const ddate = memoizedDateFilter;

      // console.log("MapContext pre-filter dates ", options.from, options.to);
      if (!options.from) {
        options.from = ddate.start;
      }
      if (!options.to) {
        options.to = ddate.end;
      }
      const from = options.from;
      const to = options.to;
      let fromDate, toDate;
      const timezone = validateTimezone(options.timezone);
      // console.log("MapContext timezone ", timezone);
      const venue = options.venue;

      // safety check
      if ((!userLocation?.lat || !userLocation?.lng) && !venue) {
        options.setLoading && options.setLoading(false);
        return;
      }
      // console.log("performing search with : ", userLocation, venue);

      // Date filter has to be considered in the selected event time zone and then convert it to utc
      if (from) {
        fromDate = DateTime.fromJSDate(from)
          .setZone(timezone, {
            keepLocalTime: true,
          })
          .toUTC()
          .toISO();
        // console.log("🗓️ Main map 'from' date:", fromDate);
      }
      if (to) {
        toDate = DateTime.fromJSDate(to)
          .setZone(timezone, {
            keepLocalTime: true,
          })
          .toUTC()
          .toISO();
      }
      // console.log("fromDate ", from, fromDate);
      // console.log("toDate ", to, toDate);
      options.setLoading && options.setLoading(true);
      let url = `${backendUrl}/events/search?includeMinifiedResults=true&`;
      if (Array.isArray(activeCategories) && activeCategories.length > 0) {
        url += `categories=${activeCategories.join(",")}&`;
      }
      const hasSearchQuery = options.searchQuery && options.searchQuery.trim() !== "";
      const isListOnlyTab = hasSearchQuery && (options.activeTab === 'account' || options.activeTab === 'venue');
      const isDefaultRange =
        options.dateFilter?.start?.toDateString?.() === memoizedDateFilter.start.toDateString() &&
        options.dateFilter?.end?.toDateString?.() === memoizedDateFilter.end.toDateString();
      // add excludePastEvents only when relevant for events
      if (!isListOnlyTab) {
        url += `excludePastEvents=true&`;
      }
      // console.log("MapContext specialDateFilter ", options.specialDateFilter);
      if (options.specialDateFilter) {
        url += `page=1&limit=all&`;
      } else {
        const reqPage = options.page || 1;
        const reqLimit = options.limit || mapPage.limit || 10;
        url += `page=${reqPage}&limit=${reqLimit}&`;
      }
      if (userLocation?.lat && userLocation?.lng) {
        url += `lat=${userLocation.lat}&lng=${userLocation.lng}&`;
      }
      url += `sort=${options.sort || "date"}&`;
      if (likedFilter) {
        url += `liked=${likedFilter}&`;
      }
      if (posterFilter) {
        url += `posterliked=${posterFilter}&`;
      }
      // omit date filters for account/venue text search
      if (!isListOnlyTab) {
        if (fromDate) {
          url += `from=${fromDate}&`;
        }
        // omit 'to' for event text search with default date range (infinite forward)
        const omitTo = hasSearchQuery && options.activeTab === 'event' && isDefaultRange;
        if (toDate && !omitTo) {
          url += `to=${toDate}`;
        }
      }
      if(tagFilter){
        // tagFilter is an array of tags
        url += `&tags=${tagFilter.join(",")}`;
      }
      // Handle search functionality
      if(hasSearchQuery){
        // Add search query parameter
        url += `&search=${encodeURIComponent(options.searchQuery.trim())}`;
        
        // When searching, we need both grouped data (for map) and flat data (for search results)
        url += `&includeGroupedData=true`;
        
      }
      console.log("HAS SEARCH QUERY ", hasSearchQuery);
      // Always include type filter when activeTab is set (for both search and no-search scenarios)
      if(options.activeTab !== 'event' || hasSearchQuery){
        url += `&type=${options.activeTab}`;
      }

      // build a request-key for comparing purpose (exclude page/limit)
      const requestKey = JSON.stringify({
        activeTab: options.activeTab,
        search: options.searchQuery || '',
        from: fromDate || '',
        to: toDate || '',
        timezone,
        categories: Array.isArray(activeCategories) ? activeCategories : [],
        tagFilter: Array.isArray(tagFilter) ? tagFilter : [],
        likedFilter: !!likedFilter,
        posterFilter: !!posterFilter,
        lat: userLocation?.lat,
        lng: userLocation?.lng,
        sort: options.sort || "date",
        special: !!options.specialDateFilter,
      });

      // If a request is in-flight with same key and a lower page, queue this one instead of aborting
      if (currentRequestRef.current && currentRequestMetaRef.current) {
        const meta = currentRequestMetaRef.current;
        const isSameKey = meta.key === requestKey;
        const newPage = options.page || 1;
        if (isSameKey && newPage > (meta.page || 1)) {
          // queue the higher page and let the current request finish; the completion handler will drain the queue
          pendingRequestRef.current = { ...options };
          // keep page loader on
          return;
        }
        // different key or not a forward page: abort the previous (superseded) request
        currentRequestRef.current.abort();
      }
      
      // Create new abort controller and increment request ID
      const abortController = new AbortController();
      currentRequestRef.current = abortController;
      const currentRequestId = ++requestIdRef.current;
      currentRequestMetaRef.current = { key: requestKey, page: options.page || 1 };
      
      // console.log("mapctx: request", url, { 
      //   activeTab: options.activeTab, 
      //   currentTabRef: activeTabRef.current,
      //   requestId: currentRequestId 
      // });

      const { data } = await axiosInstance.get(url, { signal: abortController.signal });
      
      // Track debug events from server response
      trackTransformation("MapContext", "SERVER_RESPONSE", data?.data);
      
      options.setLoading && options.setLoading(false);
      if (data.error) {
        enqueueSnackbar(data.error, { variant: "error" });
        return;
      }
      // console.log("mapctx: response sizes", {
      //   flat: data?.data?.length,
      //   grouped: data?.groupedData?.length,
      //   totalResults: data?.totalResults,
      //   page: options.page || 1,
      //   limit: options.limit || mapPage?.limit,
      //   requestId: currentRequestId,
      // });
      const flatEventsData = data.data; // Flat results for lists
      const groupedVenueData = data.groupedData; // Grouped venues for map
      let totalResults = data.totalResults;
      const minifiedResults = data.minifiedResults;
      
      // Debug: Track debug events in raw API response
      if (flatEventsData || groupedVenueData || minifiedResults) {
        const debugInFlat = flatEventsData?.filter(item => 
          (item.type === 'event' && DEBUG_EVENT_NAMES.includes(item.name)) ||
          (item.type === 'venue' && (
            item.events?.some(event => DEBUG_EVENT_NAMES.includes(event.name)) ||
            (item._matchingEvent && DEBUG_EVENT_NAMES.includes(item._matchingEvent.name))
          ))
        ) || [];
        
        const debugInGrouped = groupedVenueData?.filter(item => 
          (item.type === 'event' && DEBUG_EVENT_NAMES.includes(item.name)) ||
          (item.type === 'venue' && (
            item.events?.some(event => DEBUG_EVENT_NAMES.includes(event.name)) ||
            (item._matchingEvent && DEBUG_EVENT_NAMES.includes(item._matchingEvent.name))
          ))
        ) || [];
        
        const debugInMinified = minifiedResults?.filter(item => 
          item.events?.some(eventId => DEBUG_EVENT_NAMES.some(name => name.includes(eventId))) ||
          (item._matchingEvent && DEBUG_EVENT_NAMES.includes(item._matchingEvent.name))
        ) || [];
        
        mapDebugLogger.log(`🗺️ RAW API RESPONSE:`);
        mapDebugLogger.log(`   Flat data: ${debugInFlat.length} debug items (total: ${flatEventsData?.length || 0})`);
        mapDebugLogger.log(`   Grouped data: ${debugInGrouped.length} debug items (total: ${groupedVenueData?.length || 0})`);
        mapDebugLogger.log(`   Minified data: ${debugInMinified.length} debug items (total: ${minifiedResults?.length || 0})`);
      }
      
      // Format flat events data (for search results)
      // Track debug events before timezone conversion
      trackTransformation("MapContext", "BEFORE_TIMEZONE_CONVERSION", flatEventsData);
      
      const formattedFlatData = flatEventsData?.map((item) => {
        //~ Convert Date to Local
        if (item.type === "event") {
          convertEventDate(item);
        } else if (item.type === "venue" && item.events) {
          item.events.forEach((event) => {
            convertEventDate(event);
          });
          if (item._matchingEvent) {
            convertEventDate(item._matchingEvent);
          }
        }
        return item;
      });
      
      // Track debug events after timezone conversion
      trackTransformation("MapContext", "AFTER_TIMEZONE_CONVERSION", formattedFlatData);
      
      // Format grouped venue data (for map pins)
      const formattedGroupedData = groupedVenueData?.map((item) => {
        //~ Convert Date to Local
        if (item.type === "event") {
          convertEventDate(item);
        } else if (item.type === "venue" && item.events) {
          item.events.forEach((event) => {
            convertEventDate(event);
          });
          if (item._matchingEvent) {
            convertEventDate(item._matchingEvent);
          }
        }
        return item;
      });

      // guard against wrong-tab responses and stale requests
      if (options.activeTab !== activeTabRef.current || currentRequestId !== requestIdRef.current) {
        console.log("mapctx: discarding stale/wrong-tab response", { 
          requestTab: options.activeTab, 
          currentTab: activeTabRef.current,
          requestId: currentRequestId,
          currentRequestId: requestIdRef.current
        });
        options.setLoading && options.setLoading(false);
        return;
      }
      // Update state based on available data
      if (formattedFlatData) {
        setSearchResults(formattedFlatData);
      }
      
      // Select list data based on active tab
      let listData;
      if (options.activeTab === 'account') {
        // show only accounts in the list (accounts are returned in groupedData when includeGroupedData=true)
        listData = (formattedGroupedData || []).filter(item => item.type === 'account' || item.username);
        totalResults = listData.length;
      } else if (options.activeTab === 'venue') {
        // show venues list; prefer grouped venues
        listData = formattedGroupedData || (formattedFlatData || []).filter(item => item.type === 'venue');
        if (!formattedGroupedData) {
          totalResults = listData.length;
        }
      } else {
        // events tab uses grouped or flat based on availability
        listData = (formattedGroupedData && formattedGroupedData.length > 0)
          ? formattedGroupedData
          : formattedFlatData;
      }

      if (listData) {
        // only commit state for the current tab
        if (options.activeTab === activeTabRef.current) {
          setMapLocations((prev) => {
            const isFirstPage = (options.page || 1) === 1;
            const next = isFirstPage ? listData : [...prev, ...listData];
            // console.log("mapctx: setMapLocations", {
            //   page: options.page || 1,
            //   prevLen: prev?.length,
            //   addLen: listData?.length,
            //   nextLen: next?.length,
            // });
            return next;
          });
        }
      }

      // For accounts/venues tabs: do not update map pins
      if (options.activeTab === 'event') {
        // Prioritize minifiedResults for map markers (lightweight, venue-grouped)
        // Fall back to full grouped data, then flat data
        const mapData = (minifiedResults && minifiedResults.length > 0)
          ? minifiedResults
          : (formattedGroupedData && formattedGroupedData.length > 0)
            ? formattedGroupedData
            : formattedFlatData;
        
        console.log("🗺️ [MapContext] Setting map data:", {
          source: minifiedResults?.length ? 'minifiedResults' : formattedGroupedData?.length ? 'groupedData' : 'flatData',
          count: mapData?.length,
          sampleType: mapData?.[0]?.type,
          sampleHasMatchingEvent: !!mapData?.[0]?._matchingEvent,
          sampleEventsCount: mapData?.[0]?.events?.length
        });
        
        if (mapData) {
          // Debug: Track debug events in events tab map data
          const debugItems = mapData.filter(item => 
            (item.type === 'event' && DEBUG_EVENT_NAMES.includes(item.name)) ||
            (item.type === 'venue' && (
              item.events?.some(event => DEBUG_EVENT_NAMES.includes(event.name)) ||
              (item._matchingEvent && DEBUG_EVENT_NAMES.includes(item._matchingEvent.name))
            ))
          );
          
          if (debugItems.length > 0) {
            mapDebugLogger.log(`🗺️ EVENTS TAB: Found ${debugItems.length} debug item(s) for map`);
            debugItems.forEach(item => {
              mapDebugLogger.log(`   ${item.type}: "${item.name}" (ID: ${item._id})`);
              if (item.type === 'venue') {
                mapDebugLogger.log(`   Matching Event: "${item._matchingEvent?.name || 'none'}"`);
                mapDebugLogger.log(`   Events Count: ${item.events?.length || 0}`);
              }
            });
          } else {
            mapDebugLogger.log(`🗺️ EVENTS TAB: No debug events found in ${mapData.length} items`);
            mapDebugLogger.log(`   Data source: ${minifiedResults?.length ? 'minifiedResults' : formattedGroupedData?.length ? 'groupedData' : 'flatData'}`);
          }
          
          setMapResults(mapData);
        }
      }

      // console.log("MapContext processed data - flat:", formattedFlatData, "grouped:", formattedGroupedData);

      if (options.activeTab === activeTabRef.current) {
        options.onSuccess &&
          options.onSuccess({
            data: listData, // the list to render in the bottom sheet
            searchData: formattedFlatData,
            groupedData: formattedGroupedData,
            totalResults: totalResults,
            minifiedResults: minifiedResults,
          });
      }

      // turn off the primary loading flag AFTER state updates to avoid flashing old content
      setMapLoading(false);
      // clear the request ref since this request completed successfully
      currentRequestRef.current = null;
      currentRequestMetaRef.current = null;
      // drain queued pagination request if any
      const pending = pendingRequestRef.current;
      pendingRequestRef.current = null;
      if (pending) {
        await performSearch(pending);
      }
    } catch (error) {
      // ignore aborted requests
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.log("mapctx: request aborted");
        return;
      }
      console.log(error);
      options.setLoading && options.setLoading(false);
      setMapLoading(false);
      currentRequestRef.current = null;
      currentRequestMetaRef.current = null;
      // if there was a queued request, try to proceed
      const pending = pendingRequestRef.current;
      pendingRequestRef.current = null;
      if (pending) {
        await performSearch(pending);
      }
    }
  };

  // Removed: Auto-reset tab to 'event' when search is cleared
  // Users should be able to clear search while staying on their current tab
  // useEffect(() => {
  //   if (!searchQuery || searchQuery.trim() === "") {
  //     if (activeTab !== 'event') {
  //       setActiveTab('event');
  //     }
  //   }
  // }, [searchQuery]);

  useEffect(() => {
    if (
      user &&
      userLocation?.lat &&
      userLocation?.lng &&
      selectedTimezone &&
      dateFilter
    ) {
      const current = {
        dateFilter,
        tagFilter,
        searchQuery: debouncedSearchQuery,
        activeTab,
        userLocation,
        selectedTimezone,
        activeCategories,
        likedFilter,
        posterFilter,
        sort: effectiveSortMode,
      };
        const prev = previousFilter.current || {};
      const filtersExceptSearchAreEqual = isEqual(
            { ...prev, searchQuery: undefined },
            { ...current, searchQuery: undefined }
          );
      const searchChanged = prev.searchQuery !== debouncedSearchQuery && filtersExceptSearchAreEqual;

      // decide requested page per change type
      const requestedPage = searchChanged ? 1 : (mapPage.page || 1);

      if (requestedPage === 1) {
        // console.log("mapctx: new query - clearing locations and setting loading", { activeTab, search: debouncedSearchQuery });
        setMapLocations([]);
        setMapLoading(true);
      } else {
        setMapPageLoading(true);
      }

        fetchFilteredEvents({
          categories: activeCategories,
          from: dateFilter?.start,
          to: dateFilter?.end,
          dateFilter,
          tagFilter,
        searchQuery: debouncedSearchQuery,
          activeTab,
          setLoading: setMapPageLoading,
          timezone: selectedTimezone,
          sort: effectiveSortMode,
        debounceSearch: false, // we already debounced the value
        page: requestedPage,
        limit: mapPage.limit || 10,
        });

      // keep state in sync with the requested page
      if (mapPage.page !== requestedPage) {
        setMapPage((prevPage) => ({ ...prevPage, page: requestedPage }));
      }

      previousFilter.current = current;
    }
  }, [
    user,
    dateFilter,
    tagFilter,
    debouncedSearchQuery,
    activeTab,
    userLocation?.lat,
    selectedTimezone,
    userLocation?.lng,
    activeCategories,
    likedFilter,
    posterFilter,
    effectiveSortMode,
    mapPage.page,
    mapPage.limit,
  ]);

  useEffect(() => {
    setMapPage({
      page: 1,
      limit: 10,
    });
    if (!dateFilter) {
      setDateFilter(memoizedDateFilter);
    }
    setMapLoading(true);
  }, [
    // Removed dateFilter from dependencies to prevent circular updates
    // dateFilter should only be set by user interaction or timezone changes
    tagFilter,
    searchQuery,
    activeTab,
    userLocation?.lat,
    selectedTimezone,
    userLocation?.lng,
    activeCategories,
    likedFilter,
    posterFilter,
    effectiveSortMode,
  ]);

  useEffect(() => {
    if (selectedTimezone) {
      // console.log("MapContext selectedTimezone ", selectedTimezone);
      // Only update dateFilter if it's different from memoizedDateFilter
      // This prevents unnecessary re-renders and duplicate analytics events
      const needsUpdate = !dateFilter ||
        dateFilter.start?.toISOString() !== memoizedDateFilter.start?.toISOString() ||
        dateFilter.end?.toISOString() !== memoizedDateFilter.end?.toISOString();
      
      if (needsUpdate) {
        setDateFilter(memoizedDateFilter);
      }
    }
  }, [selectedTimezone, memoizedDateFilter]);

  useEffect(() => {
    return () => {
      debouncedPerformSearch.current && debouncedPerformSearch.current.cancel();
    };
  }, []);

  const value = {
    mapLocations,
    setMapLocations,
    dateFilter,
    setDateFilter,
    tagFilter,
    setTagFilter,
    searchQuery,
    setSearchQuery,
    sortMode,
    setSortMode,
    effectiveSortMode,
    autoSuggestion,
    setAutoSuggestion,
    activeTab,
    setActiveTab,
    mapLoading,
    setMapLoading,
    mapPageLoading,
    setMapPageLoading,
    refreshEvents,
    mapPage,
    setMapPage,
    totalResults,
    setTotalResults,
    mapResults,        // Grouped venue data for map pins
    setMapResults,
    searchResults,     // Flat event data for search/lists
    setSearchResults,
    defaultDateFilter : memoizedDateFilter,
  };
  return <mapContext.Provider value={value}>{children}</mapContext.Provider>;
};

export const useMapContext = () => {
  const context = useContext(mapContext);
  if (!context) {
    throw new Error("useMap must be used within a MapProvider");
  }
  return context;
};

// Add timezone utilities
const validateTimezone = (timezone) => {
  try {
    DateTime.local().setZone(timezone);
    return timezone;
  } catch (e) {
    console.warn(`Invalid timezone ${timezone}, falling back to UTC`);
    return 'UTC';
  }
};
